import { useTranslation } from 'react-i18next';
import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTokens } from 'theme';
import { useConversations, useDeleteConversation, useRenameConversation } from 'api/assistant';
import { formatWhen } from './copy';
import { conversationPreview, filterConversations, groupConversations } from './conversationHistory';

function DeleteDialog({ open, title, loading, error, onConfirm, onClose }) {
  const { t: translate } = useTranslation();
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{translate('assistant.conversationListDeleteChat')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{translate('assistant.deleteChatDescription', { title })}</DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{translate('assistant.conversationListCouldNotDeleteTheChat')}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={loading}>{translate('assistant.conversationListCancel')}</Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >{translate('assistant.conversationListDelete')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function RenameDialog({ conversation, loading, error, onConfirm, onClose }) {
  const { t: translate } = useTranslation();
  const [title, setTitle] = useState(conversation.title);
  const inputRef = useRef(null);
  const valid = Boolean(title.trim()) && title.trim().length <= 200;
  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      PaperProps={{ component: 'form', sx: { maxWidth: 480, borderRadius: 3 },
        onSubmit: (event) => { event.preventDefault(); if (valid && !loading) onConfirm(title.trim()); } }}>
      <DialogTitle sx={{ fontWeight: 700, px: 3, py: 2.5 }}>{translate('assistant.conversationListChatTitle')}</DialogTitle>
      <DialogContent sx={{ px: 3, pt: '24px !important', pb: 3 }}>
        <TextField autoFocus inputRef={inputRef} fullWidth disabled={loading} label={translate('assistant.conversationListTitle')} value={title} onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 200 }} helperText={translate('assistant.conversationListThisTitleWillBeKeptWhenYou')}
          FormHelperTextProps={{ sx: { mx: 0, mt: 1.25, lineHeight: 1.6 } }} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{translate('assistant.conversationListCouldNotSaveTheTitlePleaseTry')}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <Button color="inherit" onClick={onClose} disabled={loading} sx={{ minHeight: 40, px: 2.5 }}>{translate('assistant.conversationListCancel')}</Button>
        <Button type="submit" variant="contained" disabled={!valid || loading} sx={{ minHeight: 40, px: 2.5 }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>{translate('assistant.conversationListSave')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ConversationRow({ conversation, selected, menuOpen, onSelect, onActions }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const title = conversation.title || translate('assistant.conversationListNewChat');
  const preview = conversationPreview(conversation);
  return (
    <ListItem disablePadding sx={{ mb: 0.5, '&:hover .assistant-chat-actions, &:focus-within .assistant-chat-actions': { opacity: 1 } }}>
      <ListItemButton component="button" role="button" selected={selected} aria-current={selected ? 'page' : undefined}
        aria-label={translate('assistant.conversationListOpenChat', { value1: title })} onClick={() => onSelect(conversation.id)} sx={{
          width: '100%', minWidth: 0, textAlign: 'left', alignItems: 'flex-start', px: 1.5, pr: 5.5, py: 1.25,
          borderRadius: 2, border: '1px solid transparent', color: t.ink,
          transition: 'background-color 150ms ease, border-color 150ms ease',
          '&:hover': { bgcolor: t.surface },
          '&.Mui-selected': { bgcolor: `${t.highlight}`, borderColor: `${t.highlight}`, color: t.ink },
          '&.Mui-selected:hover': { bgcolor: `${t.highlight}` },
          '&.Mui-focusVisible': { outline: `2px solid ${t.focus}`, outlineOffset: -2 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography component="span" title={title} sx={{
            fontSize: '0.84rem', fontWeight: selected ? 600 : 500, lineHeight: 1.5,
            display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', overflowWrap: 'anywhere',
          }}>{title}</Typography>
          {preview && <Typography component="span" noWrap sx={{ display: 'block', mt: 0.5, fontSize: '0.73rem', lineHeight: 1.5, color: t.inkMuted }}>{preview}</Typography>}
        </Box>
      </ListItemButton>
      <Tooltip title={translate('assistant.conversationListChatActions', { value1: formatWhen(conversation.last_activity_at) })}>
        <IconButton size="small" className="assistant-chat-actions" aria-label={translate('assistant.conversationListChatActions2', { value1: title })}
          aria-haspopup="menu" aria-expanded={Boolean(menuOpen)} onClick={(event) => onActions(conversation, event.currentTarget)} sx={{
            position: 'absolute', right: 6, top: 9, width: 30, height: 30, borderRadius: 1.5, color: t.inkMuted,
            opacity: selected ? 1 : 0, '&:hover': { bgcolor: t.raised }, '@media (hover: none)': { opacity: 1 },
          }}>
          <MoreHorizRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </ListItem>
  );
}

/** Private history, with the subject first and controls available on focus. */
export default function ConversationList({ selectedId, onSelect, onNew, onDeleted }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const conversations = useConversations();
  const remove = useDeleteConversation();
  const rename = useRenameConversation();
  const [search, setSearch] = useState('');
  const [menu, setMenu] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingRename, setPendingRename] = useState(null);
  const items = conversations.data;
  const groups = groupConversations(filterConversations(items || [], search));

  const confirmDelete = () => {
    const id = pendingDelete.id;
    remove.mutate(id, { onSuccess: () => { setPendingDelete(null); onDeleted(id); } });
  };
  const confirmRename = (title) => rename.mutate({ conversationId: pendingRename.id, title }, { onSuccess: () => setPendingRename(null) });

  return (
    <Box component="nav" aria-label={translate('assistant.conversationListChatHistory')} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: t.ink, fontWeight: 700 }}>{translate('assistant.conversationListHistory')}</Typography>
        <Button fullWidth variant="outlined" color="inherit" startIcon={<AddRoundedIcon />} onClick={onNew}
          sx={{ minHeight: 42, justifyContent: 'flex-start', px: 1.5, mb: 1.5, borderRadius: 2, bgcolor: t.surface, borderColor: t.border,
            '&:hover': { bgcolor: t.raised, borderColor: t.borderField }, '& .MuiButton-startIcon': { color: t.link } }}>{translate('assistant.conversationListNewChat2')}</Button>
        <TextField fullWidth size="small" placeholder={translate('assistant.conversationListSearchChats')} value={search} onChange={(event) => setSearch(event.target.value)}
          inputProps={{ 'aria-label': translate('assistant.conversationListSearchChats') }} InputProps={{
            startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: t.inkMuted }} /></InputAdornment>,
            endAdornment: search ? <InputAdornment position="end"><IconButton size="small" aria-label={translate('assistant.conversationListClearSearch')} onClick={() => setSearch('')}><CloseRoundedIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null,
          }} sx={{ '& .MuiInputBase-root': { fontSize: '0.78rem', borderRadius: 2, bgcolor: t.surface }, '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border } }} />
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, mx: 0.75, mb: 1, px: 0.25, pb: 1,
        borderRadius: 2 }}>
        {conversations.isLoading && <Box sx={{ px: 1, pt: 1 }}>{[0, 1, 2, 3].map((index) => <Skeleton key={index} variant="rounded" height={55} sx={{ mb: 1 }} />)}</Box>}
        {conversations.isError && <Alert severity="error" sx={{ m: 1 }} action={<Button color="inherit" size="small" onClick={() => conversations.refetch()}>{translate('assistant.answerWidgetRetry')}</Button>}>{translate('assistant.conversationListCouldNotLoadChats')}</Alert>}
        {conversations.isSuccess && groups.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            {search ? <SearchRoundedIcon sx={{ color: t.inkMuted, mb: 1 }} /> : <ChatBubbleOutlineRoundedIcon sx={{ color: t.inkMuted, mb: 1 }} />}
            <Typography variant="body2" fontWeight={600}>{search ? translate('assistant.conversationListNoChatsFound') : translate('assistant.conversationListYourConversationsWillAppearHere')}</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: t.inkMuted }}>{search ? translate('assistant.conversationListTryADifferentTitleOrPhrase') : translate('assistant.conversationListStartANewChatWithAQuestion')}</Typography>
            {search && <Button size="small" onClick={() => setSearch('')} sx={{ mt: 1 }}>{translate('assistant.conversationListResetSearch')}</Button>}
          </Box>
        )}
        {groups.map((group) => (
          <Box component="section" key={group.id} aria-label={group.label}>
            <Typography component="h3" sx={{ px: 1.5, pt: 1.75, pb: 0.75, m: 0, fontSize: '0.7rem', fontWeight: 600, color: t.inkMuted }}>{group.label}</Typography>
            <List disablePadding>{group.items.map((conversation) => <ConversationRow key={conversation.id} conversation={conversation}
              selected={conversation.id === selectedId} menuOpen={menu?.item.id === conversation.id} onSelect={onSelect} onActions={(item, anchor) => setMenu({ item, anchor })} />)}</List>
          </Box>
        ))}
      </Box>
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-label': translate('assistant.conversationListChatActions3'), sx: { p: 0.5 } }}
        PaperProps={{ sx: { minWidth: 200, mt: 0.75, borderRadius: 2.5, bgcolor: t.surface, borderColor: t.borderField } }}>
        <MenuItem onClick={() => { if (menu) { rename.reset(); setPendingRename(menu.item); setMenu(null); } }} sx={{ gap: 1.25, borderRadius: 1.5, py: 1 }}>
          <EditOutlinedIcon fontSize="small" sx={{ color: t.inkMuted }} />{translate('assistant.conversationListRename')}</MenuItem>
        <MenuItem onClick={() => { if (menu) { remove.reset(); setPendingDelete(menu.item); setMenu(null); } }} sx={{ gap: 1.25, borderRadius: 1.5, py: 1, mt: 0.5, color: t.negative }}>
          <DeleteOutlineIcon fontSize="small" />{translate('assistant.conversationListDelete')}</MenuItem>
      </Menu>
      {pendingRename && <RenameDialog key={pendingRename.id} conversation={pendingRename} loading={rename.isPending} error={rename.isError}
        onConfirm={confirmRename} onClose={() => setPendingRename(null)} />}
      <DeleteDialog open={Boolean(pendingDelete)} title={pendingDelete?.title || translate('assistant.conversationListNewChat')} loading={remove.isPending} error={remove.isError}
        onConfirm={confirmDelete} onClose={() => setPendingDelete(null)} />
    </Box>
  );
}
